import { format } from 'date-fns';
import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { Alert, AlertTitle, Autocomplete } from '@mui/material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { PublicOutlined, PublicTwoTone } from '@mui/icons-material';
import { Container, PicContainer, UploadPicInput } from './styles';
import { useUniversities } from '../../hooks/universities';
import { Title } from '../../components/atoms/title';

import api from '../../services/api';

/**
 * Formats an ISO date string to the format expected by Material UI's date picker
 * @param {string} dateISO
 * @returns {string}
 */
function fixDate(dateISO) {
  // Intl fixes the timezone, but doesn't format the string in the way that Material UI expects
  // date-fns formats the string arbitrarily, but it doesn't handle timezones
  return format(
    new Date(new Date(dateISO).toLocaleString('en-us', { timeZone: 'America/Sao_Paulo' })),
    "yyyy-MM-dd'T'HH:mm"
  );
}

const schema = yup
  .object({
    locked: yup.bool(),
    name: yup.string().max(40, 'Nome do evento deve ser menor que 40 caracteres').required('Campo obrigatório'),
    description: yup.string().required('Campo obrigatório'),
    date: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .required('Campo obrigatório'),
    expiration: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .required('Campo obrigatório'),
    city_neighborhood_state: yup
      .string()
      .max(80, 'Endereço do evento deve ser menor que 80 caracteres')
      .required('Campo obrigatório'),
    street_address: yup
      .string()
      .max(80, 'Endereço do evento deve ser menor que 80 caracteres')
      .required('Campo obrigatório'),
    coordinates: yup
      .string()
      .required('Campo obrigatório')
      .test('is-valid-coordinates', 'Invalid coordinates', value => {
        const [latitude, longitude] = value.split(',');
        if (!latitude || !longitude) {
          return false;
        }
        const lat = parseFloat(latitude);
        const lon = parseFloat(longitude);
        if (isNaN(lat) || isNaN(lon)) {
          return false;
        }
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
          return false;
        }
        return true;
      }),
    expected_attendees: yup
      .number()
      .min(0, 'Número de participantes deve ser maior ou igual a 0')
      .required('Campo obrigatório'),
  })
  .required();

export function EventCreation() {
  const queryClient = useQueryClient();
  const { states, events } = useUniversities();

  const [selectedEvent, setSelectedEvent] = useState(null);

  const [eventBanner, setEventBanner] = useState('');
  const [eventBannerFile, setEventBannerFile] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const [selectedState, setSelectedState] = useState(null);
  const [isNationalEvent, setIsNationalEvent] = useState(false);

  const [createOption, setCreateOption] = useState({
    single: true,
    bulk: false,
  });
  const [bulkCreateError, setBulkCreateError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState();

  const handleFileChange = e => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const handleChangeCreateOption = e => {
    setCreateOption({
      [e.target.name === 'single' ? 'bulk' : 'single']: false,
      [e.target.name]: e.target.checked,
    });
  };

  const handleBulkCreateEvent = async csv => {
    try {
      setLoading(true);
      setBulkCreateError(null);
      const bulkFormData = new FormData();
      bulkFormData.append('file', csv);

      await api.post('admin/events/bulk', bulkFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      reset();
      toast('Eventos criados com sucesso!', { type: 'success' });
    } catch (error) {
      if (error.response?.data?.error) {
        const errorMessage = error.response.data.error.message;
        setBulkCreateError(errorMessage);
      } else {
        toast(error.message, { type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNationalEventChange = event => {
    setIsNationalEvent(event.target.checked);
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      city_neighborhood_state: '',
      coordinates: '',
      date: '',
      description: '',
      expected_attendees: '',
      expiration: '',
      name: '',
      state_id: '',
      street_address: '',
      country_id: null,
    },

    resolver: yupResolver(schema),
  });
  const handleSelectImage = event => {
    const imageFile = event.target.files[0];

    const reader = new FileReader();

    reader.readAsDataURL(imageFile);
    reader.onload = e => {
      const image = new Image();

      image.src = e.target.result;

      image.onload = () => {
        const { height, width } = image;

        if (height !== 450 || width !== 875) {
          alert('A imagem deve ter 875px de largura e 450px de altura');
          return false;
        }

        setEventBannerFile(imageFile);

        setEventBanner(URL.createObjectURL(imageFile));

        return true;
      };
    };
  };

  const handleOpenConfirmationDialog = () => {
    if (!eventBanner && !selectedEvent?.id) {
      alert('Insira uma foto de capa do evento para continuar.');
      return;
    }
    setConfirmationDialogOpen(true);
  };

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
  };

  const onSubmit = () => {
    handleOpenConfirmationDialog();
  };

  const handleChangeStateId = (_e, value, reason) => {
    if (reason === 'clear') {
      setSelectedState(null);
      return;
    }

    if (value !== null) {
      setSelectedState(value);
    }
  };

  const resetFields = () => {
    reset();
    setEventBanner('');
    setSelectedEvent(null);
    setEventBannerFile('');
  };

  const createEvent = async () => {
    setIsLoading(true);

    try {
      const data = getValues();

      if (selectedEvent?.id) {
        const [latitude, longitude] = data.coordinates.split(',');

        const payload = {
          city_neighborhood_state: data.city_neighborhood_state,
          date: new Date(data.date).toISOString(),
          description: data.description,
          expected_attendees: data.expected_attendees ? Number(data.expected_attendees) : undefined,
          expiration: new Date(data.expiration).toISOString(),
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          name: data.name,
          state_id: selectedState?.id,
          street_address: data.street_address,
          country_id: isNationalEvent ? 1 : null,
        };

        const { data: eventData } = await api.put(`admin/events/${selectedEvent.id}`, payload);

        if (eventBannerFile) {
          const formData = new FormData();
          formData.append('image', eventBannerFile);

          await api.post(`admin/events/${eventData.id}/upload`, formData);
        }
      } else {
        const createdEvent = { ...data, state_id: selectedState?.id, country_id: isNationalEvent ? 1 : null };
        createdEvent.date = new Date(createdEvent.date).toISOString();
        createdEvent.expiration = new Date(createdEvent.expiration).toISOString();

        const [latitude, longitude] = createdEvent.coordinates.split(',');
        createdEvent.latitude = parseFloat(latitude);
        createdEvent.longitude = parseFloat(longitude);
        createdEvent.expected_attendees = Number(createdEvent.expected_attendees);
        delete createdEvent.coordinates;

        const { data: newEventData } = await api.post('admin/events', createdEvent);

        const formData = new FormData();
        formData.append('image', eventBannerFile);

        await api.post(`admin/events/${newEventData.id}/upload`, formData);
      }
      queryClient.invalidateQueries({ queryKey: 'universities' });

      if (confirm('Evento criado/atualizado com sucesso! Deseja limpar os campos preenchidos?')) {
        resetFields();
      }
    } catch (error) {
      alert('Houve um erro ao criar/atualizar o evento.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutocompleteSelection = (_e, value, reason) => {
    if (reason === 'clear') {
      resetFields();
      return;
    }

    if (value !== null) {
      setSelectedEvent(value);

      const incomingDataKeys = Object.keys(value);

      incomingDataKeys.forEach(key => {
        if (key === 'latitude' || key === 'longitude') return;
        if (value[key] !== null) {
          const val = key === 'date' || key === 'expiration' ? fixDate(value[key]) : value[key];
          setValue(key, val);
        }
      });

      setValue('coordinates', `${value.latitude}, ${value.longitude}`);

      if ('state_id' in value) {
        const eventState = states.find(state => state.id === value.state_id);
        setSelectedState(eventState);
      }
    }
  };

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item>
          <Title>Eventos</Title>
        </Grid>

        <Grid item xs={12} md={12} style={{ padding: '8px 16px' }}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createOption.single}
                  onChange={handleChangeCreateOption}
                  name='single'
                  color='primary'
                />
              }
              label='Evento Único'
            />
            <FormControlLabel
              control={
                <Checkbox checked={createOption.bulk} onChange={handleChangeCreateOption} name='bulk' color='primary' />
              }
              label='Múltiplos eventos'
            />
          </FormGroup>
        </Grid>

        {createOption.bulk && (
          <Grid item xs={12} textAlign='center'>
            <Box m='auto' mt={3}>
              <Button variant='contained' component='label'>
                Fazer upload de arquivo .csv
                <input type='file' accept='.csv' hidden onChange={handleFileChange} />
              </Button>
            </Box>

            {file && (
              <Typography variant='inherit' color='textSecondary'>
                {file.name}
              </Typography>
            )}

            <Box m='auto' mt={3} mb={3}>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                onClick={() => handleBulkCreateEvent(file)}
                disabled={!file}
                loading={loading}
              >
                Criar
              </Button>
            </Box>
            {bulkCreateError && (
              <Alert severity='error'>
                <AlertTitle>Erro</AlertTitle>
                <pre style={{ fontFamily: 'Montserrat', fontSize: 14 }}>{bulkCreateError}</pre>
              </Alert>
            )}
          </Grid>
        )}

        {createOption.single && (
          <>
            <Grid item xs={12} sm={12}>
              <Autocomplete
                options={events}
                getOptionLabel={option => option.name}
                getOptionKey={option => option.id}
                fullWidth
                onChange={handleAutocompleteSelection}
                renderInput={params => <TextField {...params} label='Eventos' variant='outlined' />}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                required
                id='name'
                name='name'
                label='Nome'
                fullWidth
                margin='dense'
                {...register('name')}
                error={!!errors.name}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Typography variant='inherit' color='textSecondary'>
                {errors.name?.message}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                required
                multiline
                maxRows={10}
                id='description'
                name='description'
                label='Descrição'
                fullWidth
                margin='dense'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('description')}
                error={!!errors.description}
              />
              <Typography variant='inherit' color='textSecondary'>
                {errors.description?.message}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                type='datetime-local'
                InputLabelProps={{
                  shrink: true,
                }}
                id='date'
                name='date'
                label='Data de início'
                fullWidth
                margin='dense'
                {...register('date')}
                error={!!errors.date}
              />
              <Typography variant='inherit' color='textSecondary'>
                {errors.date?.message}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                type='datetime-local'
                InputLabelProps={{
                  shrink: true,
                }}
                id='expiration'
                name='expiration'
                label='Data de fim'
                fullWidth
                margin='dense'
                {...register('expiration')}
                error={!!errors.expiration}
              />
              <Typography variant='inherit' color='textSecondary'>
                {errors.expiration?.message}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                id='street_address'
                name='street_address'
                label='Endereço completo'
                fullWidth
                margin='dense'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('street_address')}
                error={!!errors.street_address}
              />
              <Typography variant='inherit' color='textSecondary'>
                {errors.street_address?.message}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                id='city_neighborhood_state'
                name='city_neighborhood_state'
                label='Bairro, Cidade, Estado'
                fullWidth
                margin='dense'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('city_neighborhood_state')}
                error={!!errors.city_neighborhood_state}
              />
              <Typography variant='inherit' color='textSecondary'>
                {errors.city_neighborhood_state?.message}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                id='coordinates'
                name='coordinates'
                label='Coordinates'
                fullWidth
                margin='dense'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('coordinates')}
                error={!!errors.coordinates}
              />
              <Typography variant='inherit' color='textSecondary'>
                {errors.coordinates?.message}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Button
                variant='contained'
                color='primary'
                disabled={!watch('street_address')}
                component={Link}
                target='_blank'
                href={`https://maps.google.com/maps/place/${watch('street_address')}`}
              >
                Obter Coordenadas
              </Button>
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    icon={<PublicOutlined />}
                    checkedIcon={<PublicTwoTone style={{ fill: 'green' }} />}
                    onChange={handleNationalEventChange}
                    style={{ cursor: 'pointer' }}
                  />
                }
                label={isNationalEvent ? 'Evento nacional' : 'Evento local'}
              />
            </Grid>

            <Grid xs={12} md={6} item>
              <Autocomplete
                id='state_id'
                name='state_id'
                options={states}
                getOptionLabel={option => option.name}
                onChange={handleChangeStateId}
                value={selectedState}
                renderInput={params => <TextField {...params} placeholder='Estado' variant='outlined' />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                required
                type='number'
                id='expected_attendees'
                name='expected_attendees'
                label='Número de participantes'
                fullWidth
                margin='dense'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('expected_attendees')}
                error={!!errors.expected_attendees}
              />
              <Typography variant='inherit' color='textSecondary'>
                {errors.expected_attendees?.message}
              </Typography>
            </Grid>
            {selectedEvent && (
              <Grid item xs={6} sm={3}>
                <p>Número de confirmados</p> <span>{selectedEvent.confirmations}</span>
              </Grid>
            )}
            {/* <Grid xs={12} md={6} item>
          <Autocomplete
            id='producer_id'
            name='producer_id'
            options={states}
            getOptionLabel={option => option.name}
            onChange={handleChangeStateId}
            renderInput={params => <TextField {...params} placeholder='Produtor' variant='outlined' />}
          />
        </Grid> */}

            <Grid item xs={12} sm={12}>
              <PicContainer>
                <UploadPicInput role='button' onClick={() => document.getElementById('file-input').click()}>
                  {eventBanner ? (
                    <img
                      width={300}
                      height={154}
                      style={{
                        borderRadius: '8px',
                        boxShadow: '4px 4px 4px 1px rgba(23,23,23,0.28)',
                      }}
                      src={eventBanner}
                      alt='Banner do evento'
                    />
                  ) : (
                    <AddIcon fontSize='large' htmlColor='lightgray' />
                  )}

                  <input type='file' id='file-input' style={{ display: 'none' }} onChange={handleSelectImage} />
                </UploadPicInput>
              </PicContainer>
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: 'gray' }}>A Imagem deve ter 875px de largura por 450px de altura</p>
              </div>
            </Grid>
            <Box m='auto' mt={3}>
              <Button variant='contained' color='primary' onClick={handleSubmit(onSubmit)} disabled={isLoading}>
                {isLoading
                  ? selectedEvent?.id
                    ? 'Atualizando...'
                    : 'Criando...'
                  : selectedEvent?.id
                  ? 'Atualizar'
                  : 'Criar'}
              </Button>
            </Box>
          </>
        )}
      </Grid>

      <Dialog
        open={confirmationDialogOpen}
        onClose={handleCloseConfirmationDialog}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>Confirmar criação do evento?</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Ao confirmar a criação do evento, TODOS os usuários do aplicativo terão visibilidade dele. Certifique-se de
            que as informações preenchidas estão corretas. Deseja criar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmationDialog}>Não</Button>
          <Button
            onClick={() => {
              createEvent();
              handleCloseConfirmationDialog();
            }}
            autoFocus
          >
            Sim
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
